import { useState, useRef, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Elements } from '@stripe/react-stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { Badge } from '@/components/ui/Badge'
import ContractViewer from '@/components/ContractViewer'
import { formatSortCode, formatAccountNumber, sortCodeError, accountNumberError } from '@/utils/bankDetails'
// ⚠️ ONE Stripe loader for the whole app (memory: fix_stripe_single_loader). Never add a
// second loadStripe() here.
import { getStripe } from '@/components/payment/StripePayment'
// The card fields and wording are the SAME component the standalone /setup-card page uses.
import { CardEntry, money, ordinal } from '@/components/payment/CardMandateFields'
import api from '@/services/api'

interface ContractDetails {
  status: string
  signature_reference: string
  document_type: string
  document_html: string | null
  signer_name: string
  signer_email: string
  expires_at: string
  total_views: number
  first_viewed_at: string | null
  can_sign: boolean
  message?: string
  signed_at?: string
  expired_at?: string
  dd_required?: boolean
  dd_consent?: { version: string; service_user_name: string; statement: string; guarantee: string }
  dd_debit_order_date?: number | null
  // Card route — the exact twin of dd_required above. The card is captured on THIS
  // page; there is no second email and no second link.
  card_required?: boolean
  card_monthly_amount?: number | null
  card_charge_day?: number | null
  card_consent?: { version: string; company_name: string; statement: string }
  card_verification_amount?: string
}

interface SigningResult {
  message: string
  signature_reference: string
  signer_full_name: string
  signing_date: string
  signed_at: string
  signing_ip: string
  ip_location: string
  device: string
  terms_accepted: boolean
  document_hash: string
}

export default function SignContract() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [signingStarted, setSigningStarted] = useState(false)
  const [signatureMethod, setSignatureMethod] = useState<'drawn' | 'typed'>('drawn')
  const [typedSignature, setTypedSignature] = useState('')
  const [hasRead, setHasRead] = useState(false)

  const [formData, setFormData] = useState({
    signer_full_name: '',
    signing_date: new Date().toISOString().split('T')[0],
    terms_accepted: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch contract details
  const { data: contractData, isLoading, error } = useQuery({
    queryKey: ['contract-signing', token],
    queryFn: async () => {
      const response = await api.get(`/signing/view/${token}`)
      return response.data as ContractDetails
    },
    enabled: !!token,
    retry: false
  })

  // Start signing mutation
  const startSigningMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/signing/start', {
        signing_token: token
      })
      return response.data
    },
    onSuccess: () => {
      setSigningStarted(true)
    }
  })

  // Complete signing mutation
  const completeSigningMutation = useMutation({
    mutationFn: async (data: {
      signing_token: string
      signer_full_name: string
      signing_date: string
      signature_data: string
      signature_method: string
      terms_accepted: boolean
      terms_version: string
      screen_resolution: string
    }) => {
      const response = await api.post('/signing/complete', data)
      return response.data as SigningResult
    },
    onSuccess: (data) => {
      // Redirect to success page or show success message
      setSigningComplete(true)
      setSigningResult(data)
    }
  })

  const [signingComplete, setSigningComplete] = useState(false)
  const [signingResult, setSigningResult] = useState<SigningResult | null>(null)

  // Direct Debit step (only when the backend says this contract needs a mandate)
  const [dd, setDd] = useState({ account_holder_name: '', sort_code: '', account_number: '', consent: false, debit_order_date: 27 })
  const [ddDone, setDdDone] = useState(false)
  const [ddBank, setDdBank] = useState('')
  const ddRequired = !!contractData?.dd_required
  const ddSatisfied = !ddRequired || ddDone

  const ddMutation = useMutation({
    mutationFn: async () => {
      const r = await api.post(`/signing/${token}/dd-authorization`, {
        sort_code: dd.sort_code.trim(),
        account_number: dd.account_number.trim(),
        account_holder_name: dd.account_holder_name.trim(),
        consent_given: dd.consent,
        debit_order_date: dd.debit_order_date || contractData?.dd_debit_order_date || 27,
      })
      return r.data as { success: boolean; reference: string; bank_name?: string }
    },
    onSuccess: (data) => {
      setDdDone(true)
      setDdBank(data.bank_name || '')
    },
  })
  const ddFormValid =
    dd.account_holder_name.trim().length > 1 &&
    dd.sort_code.replace(/\D/g, '').length === 6 &&
    dd.account_number.replace(/\D/g, '').length === 8 &&
    dd.consent

  // Card step (only when the backend says this contract pays by card and no card has
  // been captured for the order yet). Same shape as the Direct Debit step above: the
  // Complete button stays locked until it is done.
  const [cardDone, setCardDone] = useState<null | { card_brand?: string; card_last4?: string; charge_day?: number }>(null)
  const cardRequired = !!contractData?.card_required
  const cardSatisfied = !cardRequired || !!cardDone
  // A bank that insists on a full-page 3-D Secure redirect sends the customer back here
  // with the PaymentIntent on the query string. Pick that up so they never land on a
  // page that has forgotten where they were.
  const [cardResume] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('payment_intent') || '' } catch { return '' }
  })

  useEffect(() => {
    // Returning from a 3-D Secure redirect: the customer had already begun signing
    // before they were sent to their bank, so put the form straight back up.
    if (cardResume) setSigningStarted(true)
  }, [cardResume])

  // Initialize canvas for signature
  useEffect(() => {
    if (canvasRef.current && signatureMethod === 'drawn') {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.strokeStyle = '#1a365d'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
      }
    }
  }, [signatureMethod, signingStarted])

  // Canvas drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return
    setIsDrawing(true)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (ctx) {
      const rect = canvas.getBoundingClientRect()
      const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left
      const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top
      ctx.beginPath()
      ctx.moveTo(x, y)
    }
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (ctx) {
      const rect = canvas.getBoundingClientRect()
      const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left
      const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top
      ctx.lineTo(x, y)
      ctx.stroke()
      setHasSignature(true)
    }
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearSignature = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setHasSignature(false)
      }
    }
    setTypedSignature('')
  }

  const getSignatureData = (): string => {
    if (signatureMethod === 'drawn' && canvasRef.current) {
      return canvasRef.current.toDataURL('image/png')
    } else if (signatureMethod === 'typed' && typedSignature) {
      // Create a canvas with typed signature
      const canvas = document.createElement('canvas')
      canvas.width = 400
      canvas.height = 150
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.font = 'italic 32px "Brush Script MT", cursive, serif'
        ctx.fillStyle = '#1a365d'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2)
        return canvas.toDataURL('image/png')
      }
    }
    return ''
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.signer_full_name.trim()) {
      newErrors.signer_full_name = 'Full legal name is required'
    }

    if (!formData.signing_date) {
      newErrors.signing_date = 'Date is required'
    }

    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'You must accept the terms and conditions'
    }

    if (signatureMethod === 'drawn' && !hasSignature) {
      newErrors.signature = 'Please provide your signature'
    }

    if (signatureMethod === 'typed' && !typedSignature.trim()) {
      newErrors.signature = 'Please type your signature'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleStartSigning = () => {
    startSigningMutation.mutate()
  }

  const handleCompleteSigning = () => {
    if (!validateForm()) return

    if (ddRequired && !ddDone) {
      setErrors({ dd: 'Please set up your Direct Debit above before signing.' })
      return
    }

    if (cardRequired && !cardDone) {
      setErrors({ dd: 'Please set up your monthly card payment above before signing.' })
      return
    }

    const signatureData = getSignatureData()
    if (!signatureData) {
      setErrors({ signature: 'Failed to capture signature' })
      return
    }

    completeSigningMutation.mutate({
      signing_token: token!,
      signer_full_name: formData.signer_full_name.trim(),
      signing_date: formData.signing_date,
      signature_data: signatureData,
      signature_method: signatureMethod,
      terms_accepted: formData.terms_accepted,
      terms_version: '1.0',
      screen_resolution: `${window.screen.width}x${window.screen.height}`
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading contract details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !contractData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Contract Link</h2>
          <p className="text-gray-600">
            This contract signing link is invalid or has expired. Please contact support if you believe this is an error.
          </p>
        </Card>
      </div>
    )
  }

  // Already signed state
  if (contractData.status === 'already_signed' || contractData.status === 'signed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-green-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contract Already Signed</h2>
          <p className="text-gray-600 mb-4">
            This contract was signed on {contractData.signed_at ? new Date(contractData.signed_at).toLocaleDateString('en-GB') : 'a previous date'}.
          </p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Card>
      </div>
    )
  }

  // Expired state
  if (contractData.status === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-amber-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contract Expired</h2>
          <p className="text-gray-600 mb-4">
            This signing request expired on {contractData.expired_at ? new Date(contractData.expired_at).toLocaleDateString('en-GB') : 'a previous date'}.
            Please contact support to request a new contract.
          </p>
          <Button variant="primary" onClick={() => navigate('/contact')}>
            Contact Support
          </Button>
        </Card>
      </div>
    )
  }

  // Signing complete state
  if (signingComplete && signingResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8">
          <div className="text-center">
            <div className="text-green-500 mb-4">
              <svg className="w-20 h-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Contract Signed Successfully</h2>
            <p className="text-gray-600 mb-6">
              Thank you for signing. Your contract has been recorded and stored securely.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Signing Details</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">Reference:</dt>
                <dd className="font-medium text-gray-900">{signingResult.signature_reference}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Signed By:</dt>
                <dd className="font-medium text-gray-900">{signingResult.signer_full_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Date:</dt>
                <dd className="font-medium text-gray-900">{signingResult.signing_date}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Signed At:</dt>
                <dd className="font-medium text-gray-900">{new Date(signingResult.signed_at).toLocaleString('en-GB')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Location:</dt>
                <dd className="font-medium text-gray-900">{signingResult.ip_location}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">Device:</dt>
                <dd className="font-medium text-gray-900">{signingResult.device}</dd>
              </div>
            </dl>
          </div>

          <p className="text-sm text-gray-500 text-center mb-4">
            A confirmation email has been sent to your registered email address with a copy of the signed contract.
          </p>

          <Button variant="primary" className="w-full" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Card>
      </div>
    )
  }

  // Main signing view
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Contract Signing</h1>
          <p className="text-gray-600">
            Please review and sign the contract below
          </p>
        </div>

        {/* Contract Details Card */}
        <Card className="mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Contract Details</h2>
            <Badge variant={contractData.can_sign ? 'primary' : 'default'}>
              {contractData.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Reference</dt>
              <dd className="font-medium text-gray-900">{contractData.signature_reference}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Document Type</dt>
              <dd className="font-medium text-gray-900 capitalize">{contractData.document_type.replace('_', ' ')}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Signer Name</dt>
              <dd className="font-medium text-gray-900">{contractData.signer_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900">{contractData.signer_email}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Expires</dt>
              <dd className="font-medium text-gray-900">{new Date(contractData.expires_at).toLocaleDateString('en-GB')}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Views</dt>
              <dd className="font-medium text-gray-900">{contractData.total_views}</dd>
            </div>
          </dl>
        </Card>

        {/* Full contract — the customer must read this before signing */}
        <Card className="mb-6 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Your Contract</h2>
          <p className="text-sm text-gray-600 mb-4">Please read your full contract below, then tick the box to confirm before you sign.</p>
          {contractData.document_html ? (
            <>
              <ContractViewer html={contractData.document_html} />
              <label className="flex items-start gap-2 mt-4 text-sm text-gray-800 cursor-pointer">
                <input type="checkbox" className="mt-1 h-4 w-4" checked={hasRead} onChange={(e) => setHasRead(e.target.checked)} />
                <span>I have read the full contract above and confirm the details are correct.</span>
              </label>
            </>
          ) : (
            <Alert variant="warning">
              The contract document could not be loaded. Please contact support before signing — do not sign without reading the full contract.
            </Alert>
          )}
        </Card>

        {/* Pre-signing state - Show start button */}
        {!signingStarted && contractData.can_sign && (
          <Card className="mb-6 p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ready to Sign?</h3>
            <p className="text-gray-600 mb-6">
              By continuing you confirm you have read the full contract above. You will then provide your signature and accept the terms.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={handleStartSigning}
              isLoading={startSigningMutation.isPending}
              disabled={!hasRead || !contractData.document_html}
            >
              {hasRead ? 'Begin Signing Process' : 'Read the contract to continue'}
            </Button>
          </Card>
        )}

        {/* Signing form */}
        {signingStarted && contractData.can_sign && (
          <>
            {/* Error Alert */}
            {completeSigningMutation.isError && (
              <Alert variant="error" className="mb-6">
                {(completeSigningMutation.error as any)?.response?.data?.detail || 'Failed to complete signing. Please try again.'}
              </Alert>
            )}

            {/* Direct Debit setup — required for this contract before it can be signed */}
            {ddRequired && (
              <Card className="mb-6 p-6 border-2 border-primary-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Direct Debit Setup</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your monthly payments are collected by Direct Debit. Enter your bank details below — we verify
                  them securely before you sign.
                </p>

                {ddDone ? (
                  <Alert variant="success">
                    Direct Debit set up successfully{ddBank ? ` (${ddBank})` : ''}. You can now sign below.
                  </Alert>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account holder name <span className="text-red-500">*</span></label>
                        <Input value={dd.account_holder_name} onChange={(e) => setDd({ ...dd, account_holder_name: e.target.value })} placeholder="Name on the account" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sort code <span className="text-red-500">*</span></label>
                        <Input value={dd.sort_code} onChange={(e) => setDd({ ...dd, sort_code: formatSortCode(e.target.value) })} placeholder="00-00-00" inputMode="numeric" error={sortCodeError(dd.sort_code)} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account number <span className="text-red-500">*</span></label>
                        <Input value={dd.account_number} onChange={(e) => setDd({ ...dd, account_number: formatAccountNumber(e.target.value) })} placeholder="8 digits" inputMode="numeric" error={accountNumberError(dd.account_number)} />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Direct Debit date <span className="text-red-500">*</span></label>
                      <p className="text-xs text-gray-500 mb-2">Which day of the month your payment is collected.</p>
                      <div className="flex gap-3">
                        {[5, 27].map((d) => (
                          <label key={d} className={`flex-1 flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${dd.debit_order_date === d ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                            <input type="radio" name="dd_debit_date" checked={dd.debit_order_date === d} onChange={() => setDd({ ...dd, debit_order_date: d })} className="sr-only" />
                            <span className="font-semibold">{d}th</span>
                            <span className="text-sm">of each month</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {contractData.dd_consent && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto text-xs text-gray-600 whitespace-pre-line">
                        {contractData.dd_consent.statement}
                        {'\n\n'}
                        {contractData.dd_consent.guarantee}
                      </div>
                    )}

                    <label className="flex items-start gap-2 mt-3 text-sm text-gray-800 cursor-pointer">
                      <input type="checkbox" className="mt-1 h-4 w-4" checked={dd.consent} onChange={(e) => setDd({ ...dd, consent: e.target.checked })} />
                      <span>I confirm I am the account holder and authorise {contractData.dd_consent?.service_user_name || 'Silux Mobile'} to set up a Direct Debit on this account under the Direct Debit Guarantee.</span>
                    </label>

                    {ddMutation.isError && (
                      <Alert variant="error" className="mt-3">
                        {(ddMutation.error as any)?.response?.data?.detail || 'Could not set up the Direct Debit. Please check the details and try again.'}
                      </Alert>
                    )}

                    <Button
                      variant="primary"
                      className="mt-4"
                      onClick={() => ddMutation.mutate()}
                      isLoading={ddMutation.isPending}
                      disabled={!ddFormValid}
                    >
                      Verify &amp; set up Direct Debit
                    </Button>
                    {!ddFormValid && (
                      <p className="text-xs text-gray-500 mt-2">Enter the account holder, a 6-digit sort code, an 8-digit account number, and tick the box.</p>
                    )}
                  </>
                )}
              </Card>
            )}

            {/* Monthly card payment — required for this contract before it can be signed.
                The card twin of the Direct Debit block above: captured HERE, on the
                signing page, so the customer never needs a second email or second link. */}
            {cardRequired && (
              <Card className="mb-6 p-6 border-2 border-primary-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Monthly Card Payment Setup</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your monthly payments are taken from a card kept securely on file. Add the card below —
                  it takes a moment, and you can then sign.
                </p>

                {cardDone ? (
                  <Alert variant="success">
                    Card set up successfully
                    {cardDone.card_brand || cardDone.card_last4
                      ? ` (${(cardDone.card_brand || 'card').replace(/\b\w/g, (c) => c.toUpperCase())} ending ${cardDone.card_last4 || '••••'})`
                      : ''}
                    . The {contractData.card_verification_amount || '£0.30'} check payment has been refunded. You can now sign below.
                  </Alert>
                ) : (
                  <CardStep
                    token={token!}
                    monthlyAmount={contractData.card_monthly_amount ?? 0}
                    chargeDay={contractData.card_charge_day ?? 28}
                    checkAmount={contractData.card_verification_amount || '£0.30'}
                    consentText={contractData.card_consent?.statement || ''}
                    consentVersion={contractData.card_consent?.version || ''}
                    companyName={contractData.card_consent?.company_name || 'Silux Mobile'}
                    resumePaymentIntentId={cardResume}
                    onDone={(r) => setCardDone(r)}
                  />
                )}
              </Card>
            )}

            {/* Name and Date */}
            <Card className="mb-6 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Signer Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Legal Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.signer_full_name}
                    onChange={(e) => setFormData({ ...formData, signer_full_name: e.target.value })}
                    placeholder="Enter your full legal name"
                    className={errors.signer_full_name ? 'border-red-500' : ''}
                  />
                  {errors.signer_full_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.signer_full_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  {/* Non-editable: a contract is dated the day it is signed. We display today's
                      date read-only and submit it as-is; the customer cannot change it. */}
                  <div
                    className="flex h-[44px] items-center rounded-lg border border-gray-300 bg-gray-50 px-3 text-base text-gray-900"
                    aria-readonly="true"
                  >
                    {new Date(formData.signing_date).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This is the date you are signing the contract.
                  </p>
                </div>
              </div>
            </Card>

            {/* Signature Section */}
            <Card className="mb-6 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Signature</h3>

              {/* Signature method toggle */}
              <div className="flex space-x-4 mb-4">
                <button
                  type="button"
                  onClick={() => setSignatureMethod('drawn')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                    signatureMethod === 'drawn'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Draw Signature
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureMethod('typed')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                    signatureMethod === 'typed'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Type Signature
                </button>
              </div>

              {signatureMethod === 'drawn' ? (
                <div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={200}
                      className="w-full touch-none cursor-crosshair"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Draw your signature above using your mouse or finger
                  </p>
                </div>
              ) : (
                <div>
                  <Input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your full name as signature"
                    className="text-2xl italic"
                    style={{ fontFamily: '"Brush Script MT", cursive, serif' }}
                  />
                  {typedSignature && (
                    <div className="mt-4 p-4 border rounded-lg bg-white">
                      <p className="text-gray-500 text-sm mb-2">Preview:</p>
                      <p
                        className="text-3xl text-primary-900"
                        style={{ fontFamily: '"Brush Script MT", cursive, serif' }}
                      >
                        {typedSignature}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button
                variant="secondary"
                size="sm"
                onClick={clearSignature}
                className="mt-4"
              >
                Clear Signature
              </Button>

              {errors.signature && (
                <p className="text-red-500 text-sm mt-2">{errors.signature}</p>
              )}
            </Card>

            {/* Terms and Conditions */}
            <Card className="mb-6 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms and Conditions</h3>

              <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto mb-4 text-sm text-gray-600">
                <p className="mb-2">
                  By signing this contract, you agree to the following terms and conditions:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>You confirm that all information provided is accurate and complete.</li>
                  <li>You agree to be bound by the terms of this contract.</li>
                  <li>You understand that this electronic signature is legally binding.</li>
                  <li>You consent to the storage and processing of your signature and personal data.</li>
                  <li>You acknowledge receipt of a copy of this agreement.</li>
                </ul>
              </div>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.terms_accepted}
                  onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the terms and conditions above. I understand that this electronic signature is legally binding.
                </span>
              </label>
              {errors.terms_accepted && (
                <p className="text-red-500 text-sm mt-2">{errors.terms_accepted}</p>
              )}
            </Card>

            {/* Complete Button */}
            <div className="flex flex-col items-center">
              {errors.dd && <p className="text-red-500 text-sm mb-2">{errors.dd}</p>}
              <Button
                variant="primary"
                size="lg"
                onClick={handleCompleteSigning}
                isLoading={completeSigningMutation.isPending}
                disabled={!ddSatisfied || !cardSatisfied}
                className="px-12"
              >
                Complete Contract
              </Button>
              {ddRequired && !ddDone && (
                <p className="text-xs text-gray-500 mt-2">Set up your Direct Debit above to enable signing.</p>
              )}
              {cardRequired && !cardDone && (
                <p className="text-xs text-gray-500 mt-2">Set up your monthly card payment above to enable signing.</p>
              )}
            </div>
          </>
        )}

        {/* Cannot sign message */}
        {!contractData.can_sign && contractData.status !== 'signed' && contractData.status !== 'expired' && (
          <Card className="p-6 text-center">
            <div className="text-amber-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contract Cannot Be Signed</h3>
            <p className="text-gray-600">
              This contract is currently in "{contractData.status.replace('_', ' ')}" status and cannot be signed at this time.
              Please contact support for assistance.
            </p>
          </Card>
        )}
      </div>
    </div>
  )
}


/**
 * The inline card-on-file step. Everything the standalone /setup-card page does, in the
 * middle of the signing page: the check payment that proves the card is live (refunded
 * in the same moment), the card vaulted at Stripe, the consent tick, the audit row.
 *
 * Endpoints (token-scoped, no login — the signing token IS the authorisation):
 *   POST /signing/{token}/card-setup-intent   → verification PaymentIntent
 *   POST /signing/{token}/card-authorization  → store the card + record consent
 *
 * ⚠️ No card number ever reaches this page: it only exists inside Stripe's iframe.
 */
function CardStep({
  token,
  monthlyAmount,
  chargeDay,
  checkAmount,
  consentText,
  consentVersion,
  companyName,
  resumePaymentIntentId,
  onDone,
}: {
  token: string
  monthlyAmount: number
  chargeDay: number
  checkAmount: string
  consentText: string
  consentVersion: string
  companyName: string
  resumePaymentIntentId: string
  onDone: (r: { card_brand?: string; card_last4?: string; charge_day?: number }) => void
}) {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [stripeFailed, setStripeFailed] = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [intentError, setIntentError] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  // Bumped to force a brand-new PaymentElement when the customer tries another card.
  const [attempt, setAttempt] = useState(0)
  const creatingIntent = useRef(false)

  const dayText = `${ordinal(chargeDay)} of each month`

  // Stripe.js, from the ONE shared loader.
  useEffect(() => {
    let active = true
    let settled = false
    const timer = setTimeout(() => { if (active && !settled) setStripeFailed(true) }, 12000)
    getStripe()
      .then((s) => { settled = true; if (active) { setStripe(s); if (!s) setStripeFailed(true) } })
      .catch(() => { settled = true; if (active) setStripeFailed(true) })
    return () => { active = false; clearTimeout(timer) }
  }, [])

  const createIntent = useCallback(async () => {
    // Guard: clearing the client secret re-triggers the effect below, so without this
    // a "try another card" would open two verification intents.
    if (creatingIntent.current) return
    creatingIntent.current = true
    setIntentError('')
    setClientSecret('')
    try {
      const r = await api.post(`/signing/${token}/card-setup-intent`, {})
      setClientSecret(r.data?.client_secret || '')
      if (!r.data?.client_secret) setIntentError('We could not open the secure card form. Please try again shortly.')
    } catch (e: any) {
      setIntentError(
        e?.response?.data?.detail ||
        'We could not open the secure card form. Please try again shortly, or contact us and we will help.',
      )
    } finally {
      creatingIntent.current = false
    }
  }, [token])

  useEffect(() => {
    if (!clientSecret && !intentError) void createIntent()
  }, [clientSecret, intentError, createIntent])

  const finalise = useCallback(async (paymentIntentId: string) => {
    const r = await api.post(`/signing/${token}/card-authorization`, {
      payment_intent_id: paymentIntentId,
      consent_given: true,
    })
    onDone(r.data || {})
  }, [token, onDone])

  // Back from a full-page 3-D Secure redirect: finish the capture straight away.
  const resumed = useRef(false)
  useEffect(() => {
    if (!resumePaymentIntentId || resumed.current) return
    resumed.current = true
    const params = new URLSearchParams(window.location.search)
    const status = params.get('redirect_status')
    window.history.replaceState({}, '', window.location.pathname)
    if (status && status !== 'succeeded') {
      setFormError('The security check with your bank was not completed, so nothing has been set up. Please try again.')
      return
    }
    setSubmitting(true)
    finalise(resumePaymentIntentId)
      .catch((e: any) => setFormError(
        e?.response?.data?.detail || 'Your card was authorised but we could not finish the setup. Please contact us before trying again.',
      ))
      .finally(() => setSubmitting(false))
  }, [resumePaymentIntentId, finalise])

  const tryAnotherCard = () => {
    setFormError('')
    setAttempt((a) => a + 1)
    void createIntent()
  }

  return (
    <>
      {/* Plain-English summary, ABOVE the card form on purpose: nobody should have to
          open a consent box to find out what is being taken, when, or what happens to
          their card number. Same wording as the standalone /setup-card page. */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-semibold mb-2">What you are agreeing to</p>
        <ul className="space-y-2 list-disc pl-4">
          <li>
            We take a <b>{checkAmount} check payment</b> now, purely to confirm the card is live.
            <b> It is refunded straight away</b> — it comes back to you.
          </li>
          <li>
            Your card is then <b>stored securely with Stripe</b>, our payment provider.
            <b> {companyName} only keeps the last 4 digits and the expiry date</b> — we never see or
            store your full card number.
          </li>
          <li>
            We will charge <b>{money(monthlyAmount)} a month</b>, on the <b>{dayText}</b>.
          </li>
          <li>
            This <b>repeats every month until your contract ends, or until you tell us to stop</b>.
          </li>
          <li>
            To stop it, or to change the card, email <b>support@siluxmobile.co.uk</b> or call{' '}
            <b>01246 912899</b> — we will confirm in writing. Stopping the card payment does not
            cancel your contract, so anything still owed remains due.
          </li>
        </ul>
      </div>

      {/* The authoritative wording is whatever the backend records against the consent
          version — render it, never a copy of our own. */}
      {consentText && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3 max-h-44 overflow-y-auto text-xs text-gray-600 whitespace-pre-line">
          {consentText}
          {consentVersion && (
            <div className="mt-2 text-[11px] text-gray-400">Version {consentVersion}</div>
          )}
        </div>
      )}

      <label className="flex items-start gap-2 mt-3 text-sm text-gray-800 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          I authorise {companyName} to take a {checkAmount} check payment now (refunded straight away),
          to store this card securely with Stripe — keeping only the last 4 digits and expiry
          themselves, never the full number — and to charge {money(monthlyAmount)} on the {dayText} until
          my contract ends or I tell them to stop. I know I can stop it at any time by contacting them.
        </span>
      </label>

      <div className="mt-4">
        {stripeFailed || (!stripe && intentError) ? (
          <Alert variant="error">
            We couldn't load the secure card form. This is almost always an ad-blocker or privacy
            extension blocking our payment provider — please pause it and refresh, or try another
            browser. You can also call us on 01246 912899 and we will take the details over the phone.
          </Alert>
        ) : intentError ? (
          <Alert variant="error">{intentError}</Alert>
        ) : !stripe || !clientSecret ? (
          <div className="text-center py-6"><Spinner size="lg" /></div>
        ) : (
          <Elements
            key={`${clientSecret}-${attempt}`}
            stripe={stripe}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe' as const,
                variables: {
                  colorPrimary: '#40A345',
                  colorText: '#1f2937',
                  colorDanger: '#ef4444',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  borderRadius: '0.5rem',
                },
              },
            }}
          >
            <CardEntry
              amountLabel={checkAmount}
              consent={consent}
              submitting={submitting}
              setSubmitting={setSubmitting}
              formError={formError}
              setFormError={setFormError}
              onAuthorised={finalise}
              onTryAnother={tryAnotherCard}
            />
          </Elements>
        )}
      </div>

      <p className="text-[11px] text-gray-400 mt-4 text-center">
        Card details are entered directly into Stripe's secure form. They never touch {companyName}'s
        systems.
      </p>
    </>
  )
}
